import { Component, type ErrorInfo, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { captureError } from "@/lib/telemetry";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

function sanitizeErrorMessage(msg: string | undefined): string {
  if (!msg) return "";
  const firstLine = msg.split(/\r?\n/)[0] ?? "";
  const withoutStack = firstLine.replace(/\s+at\s+.*/i, "").trim();
  return withoutStack.slice(0, 200);
}

/** Catches React render errors and shows a fallback UI so the app doesn’t go blank. */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    captureError(error, { componentStack: info.componentStack });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-background">
          <div className="max-w-md w-full text-center space-y-4">
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto bg-destructive/15 text-destructive">
              <AlertTriangle className="w-7 h-7" />
            </div>
            <h1 className="text-xl font-semibold text-text-primary">
              Something went wrong
            </h1>
            <p className="text-sm text-text-muted">
              {import.meta.env.DEV && this.state.error?.message
                ? sanitizeErrorMessage(this.state.error.message) || "An unexpected error occurred."
                : "An unexpected error occurred. Please try again."}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                variant="outline"
                onClick={() => window.location.reload()}
                className="gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Reload page
              </Button>
              <Button
                variant="secondary"
                asChild
                className="gap-2"
              >
                <a href="/">Go home</a>
              </Button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/** Route-level error boundary: isolates page crashes without replacing the full layout. */
class RouteErrorBoundaryInner extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    captureError(error, { componentStack: info.componentStack, boundary: "route" });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center p-8 min-h-[40vh]">
          <div className="max-w-md w-full text-center space-y-4">
            <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto bg-destructive/15 text-destructive">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <h2 className="text-lg font-semibold text-text-primary">
              This page encountered an error
            </h2>
            <p className="text-sm text-text-muted">
              {import.meta.env.DEV && this.state.error?.message
                ? sanitizeErrorMessage(this.state.error.message) || "An unexpected error occurred."
                : "An unexpected error occurred. Please try again."}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.location.reload()}
                className="gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Try again
              </Button>
              <Button variant="secondary" size="sm" asChild className="gap-2">
                <Link to="/">
                  <Home className="w-4 h-4" />
                  Go home
                </Link>
              </Button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/** Wrapper that resets the boundary when the route changes (avoids stale error state on navigation). */
export function RouteErrorBoundary({ children }: Props) {
  const location = useLocation();
  return (
    <RouteErrorBoundaryInner key={location.pathname}>
      {children}
    </RouteErrorBoundaryInner>
  );
}
